import { AlertTriangle, ArrowLeft, Clock, Copy, Download, FileSpreadsheet, GraduationCap, LayoutDashboard, LogOut, Mail, Network, Upload, UserPlus, Users } from 'lucide-react'
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ApiError, apiErrorMessage, getAggregatorAccountType, getAggregatorToken, setAggregatorToken } from '../../api/client'
import { aggregatorOrgApi } from '../../api/endpoints'
import { ErrorMessage } from '../../components/ErrorMessage'
import type { AggregatorOrgProfile, CoordinatorBulkInviteResponse, OrgCoordinatorResponse, OrgInviteResponse } from '../../types/api'
import { OrgProgress } from './OrgProgress'
import { AGGREGATOR_KINDS } from './aggregatorKinds'
import { NetworkOverview, PeopleSection, Pill, TabButton, Tile } from './portalShared'
import { STATUS, saveBlob } from './portalUtils'

type Tab = 'overview' | 'invite'

/**
 * The organisation's dashboard. It does two things only: invite coordinators, and track everything
 * they bring in - per coordinator, and drilled down to each one's people and Signals network status.
 * Uploads, links and QR codes belong to the coordinators.
 */
export function AggregatorOrgPage() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('overview')
  const [me, setMe] = useState<AggregatorOrgProfile | null>(null)
  const [coordinators, setCoordinators] = useState<OrgCoordinatorResponse[]>([])
  const [selected, setSelected] = useState<OrgCoordinatorResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const signOut = useCallback(() => {
    setAggregatorToken(null)
    navigate('/aggregator-portal/login', { replace: true })
  }, [navigate])

  const onError = useCallback(
    (err: unknown, fallback: string): string | null => {
      if (err instanceof ApiError && err.status === 401) {
        signOut()
        return null
      }
      return apiErrorMessage(err, fallback)
    },
    [signOut],
  )

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [profile, list] = await Promise.all([aggregatorOrgApi.me(), aggregatorOrgApi.coordinators()])
      setMe(profile)
      setCoordinators(list)
      setError(null)
    } catch (err) {
      setError(onError(err, 'Could not load your organisation'))
    } finally {
      setLoading(false)
    }
  }, [onError])

  useEffect(() => {
    if (getAggregatorToken() && getAggregatorAccountType() === 'ORG') void load()
  }, [load])

  if (!getAggregatorToken()) return <Navigate to="/aggregator-portal/login" replace />
  if (getAggregatorAccountType() !== 'ORG') return <Navigate to="/aggregator-portal" replace />

  const kindLabel = AGGREGATOR_KINDS.find((k) => k.kind === me?.kind)?.label
  const onNetworkTotal = coordinators.reduce((n, c) => n + (c.networkUsers ?? 0), 0)

  return (
    <div className="min-h-screen bg-ink-100">
      <header className="border-b border-ink-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-2">
            <Network size={18} className="text-sun-600" />
            <span className="font-bold text-ink-900">{me?.name ?? 'Organisation'}</span>
            {kindLabel && <span className="text-sm text-ink-500">· {kindLabel}</span>}
          </div>
          <nav className="flex flex-wrap items-center gap-1">
            <TabButton
              icon={<LayoutDashboard size={14} />}
              label="Overview"
              current={tab === 'overview'}
              onClick={() => { setTab('overview'); setSelected(null) }}
            />
            <TabButton icon={<UserPlus size={14} />} label="Invite coordinators" current={tab === 'invite'} onClick={() => setTab('invite')} />
            <button onClick={signOut} className="btn-ghost gap-1.5">
              <LogOut size={14} /> Sign out
            </button>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-5 px-4 py-6">
        <ErrorMessage message={error} />
        {me?.approvalStatus === 'SUSPENDED' && (
          <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <p>This organisation is suspended: no new invitations, and your coordinators cannot upload. Contact support.</p>
          </div>
        )}
        {me?.approvalStatus === 'PENDING' && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <Clock size={16} className="mt-0.5 shrink-0" />
            <p>
              <strong>Waiting for approval.</strong> Our team is reviewing your application. You will be emailed when it
              is approved - then you can invite coordinators.
            </p>
          </div>
        )}
        {/* Only while invites wait on it: an organisation the sync does not cover (signed up before its
            cut-off, or with the sync off) is never connected, and would otherwise read "connecting" for good. */}
        {me?.approvalStatus === 'APPROVED' && !me.invitesOpen && !['registered', 'registered_as_aggregator'].includes(me.dpgStatus ?? '') && (
          <div
            className={`flex items-start gap-2 rounded-xl border p-4 text-sm ${
              me.dpgStatus === 'failed' || me.dpgStatus === 'exists'
                ? 'border-red-200 bg-red-50 text-red-900'
                : 'border-amber-200 bg-amber-50 text-amber-900'
            }`}
          >
            <Clock size={16} className="mt-0.5 shrink-0" />
            <p>
              {me.dpgStatus === 'failed' || me.dpgStatus === 'exists' ? (
                <>
                  <strong>Connecting to the DPG network did not go through.</strong>
                  {me.dpgSyncError ? ` ${me.dpgSyncError}.` : ''} Our team can see this and will retry it.
                </>
              ) : (
                <>
                  <strong>Approved - connecting you to the DPG network.</strong> This usually takes a couple of
                  minutes; refresh to check.
                </>
              )}
            </p>
          </div>
        )}
        {me?.approvalStatus === 'REJECTED' && (
          <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <p>
              <strong>Your application was not approved.</strong>
              {me.decisionReason ? ` Reason: ${me.decisionReason}.` : ''} If you think this is a mistake, contact support.
            </p>
          </div>
        )}

        {me && tab === 'overview' && !selected && (
          <>
            <OrgProgress me={me} />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Tile icon={<UserPlus size={16} />} label="Coordinators" value={me.coordinators} />
              <Tile icon={<Users size={16} />} label="Students brought in" value={me.studentsOnboarded} />
              <Tile icon={<GraduationCap size={16} />} label="Tutors brought in" value={me.tutorsOnboarded} />
              <Tile icon={<Network size={16} />} label="On the Signals network" value={onNetworkTotal} />
            </div>

            <section className="card-pad">
              <h2 className="font-bold text-ink-900">Coordinators</h2>
              <p className="text-sm text-ink-500">Open one to see the people it brought in and how they are doing on the network.</p>
              {loading && coordinators.length === 0 ? (
                <p className="mt-3 text-sm text-ink-500">Loading...</p>
              ) : coordinators.length === 0 ? (
                <p className="mt-3 text-sm text-ink-500">
                  No coordinators yet.{' '}
                  <button className="font-semibold text-ink-900 underline" onClick={() => setTab('invite')}>
                    Invite your first coordinator
                  </button>
                  .
                </p>
              ) : (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="text-xs uppercase text-ink-400">
                      <tr>
                        <th className="py-2 pr-3">Coordinator</th>
                        <th className="py-2 pr-3">Type</th>
                        <th className="py-2 pr-3">Contact</th>
                        <th className="py-2 pr-3">Brought in</th>
                        <th className="py-2 pr-3">On network</th>
                        <th className="py-2 pr-3">Status on network</th>
                        <th className="py-2" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-100">
                      {coordinators.map((c) => (
                        <tr key={c.id}>
                          <td className="py-2 pr-3 font-medium text-ink-900">
                            {c.name}
                            {c.approvalStatus === 'SUSPENDED' && <span className="ml-2 text-xs text-red-700">suspended</span>}
                          </td>
                          <td className="py-2 pr-3 text-ink-600">{audienceLabel(c.audience)}</td>
                          <td className="py-2 pr-3 text-xs text-ink-600">
                            {c.contactName ?? '-'}
                            <br />
                            {c.email ?? ''}
                          </td>
                          <td className="py-2 pr-3 text-ink-600">{c.peopleCount}</td>
                          <td className="py-2 pr-3 text-ink-600">{c.onNetwork ? c.networkUsers ?? '-' : 'Not connected'}</td>
                          <td className="py-2 pr-3">
                            <div className="flex flex-wrap gap-1">
                              {c.networkByStatus
                                ? STATUS.map((s) => (
                                    <Pill key={s.key} className={s.className}>
                                      {s.label} {c.networkByStatus?.[s.key] ?? 0}
                                    </Pill>
                                  ))
                                : '-'}
                            </div>
                          </td>
                          <td className="py-2 text-right">
                            <button className="btn-ghost !py-1.5" onClick={() => setSelected(c)}>
                              Open
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}

        {me && tab === 'overview' && selected && (
          <CoordinatorDetail coordinator={selected} onBack={() => setSelected(null)} onError={onError} />
        )}

        {me && tab === 'invite' && !me.invitesOpen && (
          <div className="card-pad text-sm text-ink-600">
            <h2 className="font-bold text-ink-900">Invitations are not open yet</h2>
            <p className="mt-1">
              {me.approvalStatus !== 'APPROVED'
                ? 'You can invite coordinators once your organisation is approved.'
                : 'You can invite coordinators as soon as your organisation is connected to the DPG network - usually a couple of minutes after approval.'}
            </p>
          </div>
        )}
        {me && tab === 'invite' && me.invitesOpen && <InviteTab orgName={me.name} onError={onError} onInvited={() => void load()} />}
      </main>
    </div>
  )
}

function audienceLabel(audience?: string | null): string {
  if (audience === 'STUDENTS') return 'Seekers (students)'
  if (audience === 'TUTORS') return 'Providers (tutors)'
  return 'Students & tutors'
}

/** One coordinator, read-only: its people here and its people on the network. */
function CoordinatorDetail({
  coordinator,
  onBack,
  onError,
}: {
  coordinator: OrgCoordinatorResponse
  onBack: () => void
  onError: (err: unknown, fallback: string) => string | null
}) {
  const id = coordinator.id
  const loadNetwork = useCallback(
    (params: { page: number; limit: number; status?: string }) => aggregatorOrgApi.networkDashboard(id, params),
    [id],
  )
  const loadPeople = useCallback(() => aggregatorOrgApi.people(id), [id])
  const isTutors = coordinator.audience === 'TUTORS'

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button className="btn-ghost gap-1.5" onClick={onBack}>
            <ArrowLeft size={14} /> All coordinators
          </button>
          <h2 className="mt-2 text-lg font-bold text-ink-900">{coordinator.name}</h2>
          <p className="text-sm text-ink-500">
            {audienceLabel(coordinator.audience)} · {coordinator.contactName ?? '-'} · {coordinator.email ?? '-'}
            {coordinator.website ? ` · ${coordinator.website}` : ''}
          </p>
        </div>
      </div>
      <NetworkOverview
        load={loadNetwork}
        exportCsv={(status) => aggregatorOrgApi.networkExport(id, status)}
        exportName={coordinator.name}
        onError={onError}
        title={`${coordinator.name} on the Signals network`}
      />
      <PeopleSection
        title={isTutors ? 'Tutors brought in' : coordinator.audience === 'STUDENTS' ? 'Students brought in' : 'People brought in'}
        subtitle="By upload, or through the coordinator's invite link or QR. Most recent first."
        isTutors={isTutors}
        load={loadPeople}
        onError={onError}
      />
    </div>
  )
}

function InviteTab({
  orgName,
  onError,
  onInvited,
}: {
  orgName: string
  onError: (err: unknown, fallback: string) => string | null
  onInvited: () => void
}) {
  const [email, setEmail] = useState('')
  const [contactName, setContactName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<OrgInviteResponse | null>(null)
  const [invites, setInvites] = useState<OrgInviteResponse[]>([])
  const [copied, setCopied] = useState(false)

  const loadInvites = useCallback(async () => {
    try {
      setInvites(await aggregatorOrgApi.invites())
    } catch (err) {
      setError(onError(err, 'Could not load your invitations'))
    }
  }, [onError])

  useEffect(() => {
    void loadInvites()
  }, [loadInvites])

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!email.trim()) {
      setError('Enter the coordinator’s email')
      return
    }
    setSubmitting(true)
    setError(null)
    setCreated(null)
    try {
      const invite = await aggregatorOrgApi.invite(email.trim(), contactName.trim() || undefined)
      setCreated(invite)
      setEmail('')
      setContactName('')
      void loadInvites()
      onInvited()
    } catch (err) {
      setError(onError(err, 'Could not send the invitation'))
    } finally {
      setSubmitting(false)
    }
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="space-y-5">
      <section className="card-pad">
        <h2 className="font-bold text-ink-900">Invite a coordinator</h2>
        <p className="text-sm text-ink-500">
          A coordinator runs one centre, division or programme for {orgName}. They get an email with a sign-up link,
          choose whether they onboard students (Seekers) or tutors (Providers), and then bring people in by upload,
          invite link and QR. Everything they bring in shows up on your Overview.
        </p>
        <form onSubmit={submit} className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <label className="block">
            <span className="label label-required">Coordinator email</span>
            <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="block">
            <span className="label">Name (optional)</span>
            <input className="input" value={contactName} onChange={(e) => setContactName(e.target.value)} />
          </label>
          <button type="submit" disabled={submitting} className="btn-sun gap-1.5">
            <Mail size={14} /> {submitting ? 'Sending...' : 'Send invitation'}
          </button>
        </form>
        <div className="mt-3">
          <ErrorMessage message={error} />
        </div>
        {created?.inviteUrl && (
          <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">
            <p>
              Invitation sent to <span className="font-semibold">{created.email}</span>. You can also share this link
              yourself (valid for 7 days, shown only now):
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <code className="min-w-0 flex-1 break-all rounded border border-green-200 bg-white px-2 py-1 text-xs">{created.inviteUrl}</code>
              <button className="btn-ghost gap-1.5" onClick={() => void copy(created.inviteUrl!)}>
                <Copy size={14} /> {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            {created.inviteCode && (
              <p className="mt-2">
                Or they can enter the invitation code{' '}
                <code className="rounded border border-green-200 bg-white px-1.5 py-0.5 font-bold tracking-widest">
                  {created.inviteCode}
                </code>{' '}
                at <span className="font-semibold">/aggregator-portal/join</span>.
              </p>
            )}
          </div>
        )}
      </section>

      <BulkInvite onError={onError} onDone={() => { void loadInvites(); onInvited() }} />

      <section className="card-pad">
        <h2 className="font-bold text-ink-900">Invitations</h2>
        {invites.length === 0 ? (
          <p className="mt-2 text-sm text-ink-500">None sent yet.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase text-ink-400">
                <tr>
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2">Sent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {invites.map((i) => (
                  <tr key={i.id}>
                    <td className="py-2 pr-3 text-ink-900">{i.email}</td>
                    <td className="py-2 pr-3 text-ink-600">{i.contactName ?? '-'}</td>
                    <td className="py-2 pr-3">
                      {i.status === 'ACCEPTED' ? (
                        <Pill className="bg-green-100 text-green-800">Joined</Pill>
                      ) : i.status === 'EXPIRED' ? (
                        <Pill className="bg-ink-100 text-ink-600">Expired - invite again</Pill>
                      ) : (
                        <Pill className="bg-amber-100 text-amber-800">Pending</Pill>
                      )}
                    </td>
                    <td className="py-2 text-ink-500">{new Date(i.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

/**
 * Invite many coordinators at once from a CSV or Excel file - columns email and name. Each row is
 * invited exactly as the single form does it; the report says what happened to every row, with the
 * link for each new invitation (shown this once, as for a single invite).
 */
function BulkInvite({
  onError,
  onDone,
}: {
  onError: (err: unknown, fallback: string) => string | null
  onDone: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<CoordinatorBulkInviteResponse | null>(null)

  async function upload() {
    if (!file) return
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      setResult(await aggregatorOrgApi.inviteAll(file))
      setFile(null)
      onDone()
    } catch (err) {
      setError(onError(err, 'Could not read that file'))
    } finally {
      setBusy(false)
    }
  }

  async function template() {
    try {
      saveBlob(await aggregatorOrgApi.inviteTemplate(), 'coordinator-invites.csv')
    } catch (err) {
      setError(onError(err, 'Could not download the template'))
    }
  }

  return (
    <section className="card-pad">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-bold text-ink-900">Bulk invite coordinators</h2>
          <p className="text-sm text-ink-500">
            Upload a CSV or Excel file with an <code>email</code> column and an optional <code>name</code> column - one
            coordinator per row. Everyone gets the same invitation email as above.
          </p>
        </div>
        <button onClick={() => void template()} className="btn-ghost gap-1.5">
          <Download size={14} /> Download template
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed border-ink-200 px-3 py-2.5 text-sm transition hover:border-sun-400">
          <input
            type="file"
            accept=".csv,text/csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="sr-only"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null)
              setResult(null)
            }}
          />
          <FileSpreadsheet size={16} className="shrink-0 text-ink-400" />
          <span className="truncate text-ink-700">{file ? file.name : 'Choose a CSV or Excel file'}</span>
        </label>
        <button disabled={!file || busy} onClick={() => void upload()} className="btn-sun gap-1.5 disabled:opacity-40">
          <Upload size={14} /> {busy ? 'Inviting...' : 'Invite all'}
        </button>
      </div>

      <div className="mt-3">
        <ErrorMessage message={error} />
      </div>

      {result && (
        <div className="mt-3">
          <div className="flex flex-wrap gap-2 text-xs">
            <Pill className="bg-green-100 text-green-800">{result.invited} invited</Pill>
            <Pill className="bg-amber-100 text-amber-800">{result.skipped} skipped</Pill>
            <Pill className="bg-red-100 text-red-800">{result.failed} failed</Pill>
          </div>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase text-ink-400">
                <tr>
                  <th className="py-2 pr-3">Row</th>
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Result</th>
                  <th className="py-2">Invite link (shown once)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {result.rows.map((r) => (
                  <tr key={r.line}>
                    <td className="py-2 pr-3 text-ink-500">{r.line}</td>
                    <td className="py-2 pr-3 text-ink-900">{r.email || '-'}</td>
                    <td className="py-2 pr-3 text-ink-600">{r.contactName || '-'}</td>
                    <td className="py-2 pr-3">
                      {r.outcome === 'INVITED' ? (
                        <Pill className="bg-green-100 text-green-800">Invited</Pill>
                      ) : (
                        <span className="text-xs">
                          <Pill className={r.outcome === 'SKIPPED' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}>
                            {r.outcome === 'SKIPPED' ? 'Skipped' : 'Failed'}
                          </Pill>{' '}
                          <span className="text-ink-500">{r.message}</span>
                        </span>
                      )}
                    </td>
                    <td className="py-2">
                      {r.inviteUrl ? <code className="break-all text-xs text-ink-700">{r.inviteUrl}</code> : '-'}
                      {r.inviteCode && <div className="mt-0.5 text-xs font-bold tracking-widest text-ink-900">{r.inviteCode}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  )
}
